import React from 'react'
import { Box, Button, Drawer, Flex, HStack, Text, VStack, Separator } from '@chakra-ui/react'
import {
  LuX,
  LuLayoutDashboard,
  LuCircleHelp,
  LuSettings,
  LuStore,
  LuUser,
  LuLogOut,
  LuShieldQuestion,
} from 'react-icons/lu'
import { UnseenIndicatorDot } from '@/components/common/UnseenIndicatorDot'

type MobileSidebarProps = {
  isOpen: boolean
  onClose: () => void
  userName?: string
  onDashboard: () => void
  onMarketplace: () => void
  onSupport: () => void
  onSettings: () => void
  onProfile: () => void
  onLogout: () => void
  onRestartOnboarding: () => void
  /** Number of marketplace listings the SIB has not opened yet (SIB-855). */
  unseenMarketplaceCount?: number
}

export const MobileSidebar = ({
  isOpen,
  onClose,
  userName,
  onDashboard,
  onMarketplace,
  onSupport,
  onSettings,
  onProfile,
  onLogout,
  onRestartOnboarding,
  unseenMarketplaceCount = 0,
}: MobileSidebarProps): React.ReactElement => {
  const hasUnseenMarketplace = unseenMarketplaceCount > 0

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Text>{children}</Text>
  )

  const NavItem = ({
    icon,
    label,
    onClick,
    showIndicator = false,
    indicatorTestId,
    ariaLabel,
  }: {
    icon: React.ReactElement
    label: string
    onClick: () => void
    showIndicator?: boolean
    indicatorTestId?: string
    ariaLabel?: string
  }) => (
    <Button
      variant="ghost"
      colorPalette="gray"
      width="full"
      justifyContent="flex-start"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <UnseenIndicatorDot show={showIndicator} data-testid={indicatorTestId}>
        {icon}
      </UnseenIndicatorDot>
      <Text>{label}</Text>
    </Button>
  )

  const closeThen = (fn: () => void) => () => {
    fn()
    onClose()
  }

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={e => !e.open && onClose()}
      // placement="left"
      size="full"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content>
          {/* Close */}
          <Drawer.CloseTrigger asChild>
            <Button
              variant="ghost"
              colorPalette="gray"
              position="absolute"
              top={6}
              right={6}
              aria-label="Close"
            >
              <LuX />
            </Button>
          </Drawer.CloseTrigger>

          <Flex direction="column" height="100%">
            {/* Content */}
            <Box flex="1" overflowY="auto" pr={1}>
              <SectionTitle>Navigation</SectionTitle>
              <VStack align="stretch" gap={1}>
                <NavItem
                  icon={<LuLayoutDashboard />}
                  label="Dashboard"
                  onClick={closeThen(onDashboard)}
                />
                {/* Order mirrors the desktop sidebar: Dashboard, Marktplatz, Support. */}
                <NavItem
                  icon={<LuStore />}
                  label="Marktplatz"
                  onClick={closeThen(onMarketplace)}
                  showIndicator={hasUnseenMarketplace}
                  indicatorTestId="marketplace-unseen-indicator-mobile"
                  ariaLabel={
                    hasUnseenMarketplace
                      ? `Marktplatz, ${unseenMarketplaceCount} neue Ausschreibungen`
                      : undefined
                  }
                />
                <NavItem
                  icon={<LuCircleHelp />}
                  label="Support & Feedback"
                  onClick={closeThen(onSupport)}
                />
              </VStack>

              <SectionTitle>Einrichtung</SectionTitle>
              <VStack align="stretch" gap={1}>
                <NavItem
                  icon={<LuSettings />}
                  label="Einstellungen"
                  onClick={closeThen(onSettings)}
                />
                <NavItem
                  icon={<LuUser />}
                  label="Kontoeinstellungen"
                  onClick={closeThen(onProfile)}
                />
              </VStack>

              <SectionTitle>Konto</SectionTitle>
              <VStack align="stretch" gap={1}>
                <NavItem icon={<LuLogOut />} label="Abmelden" onClick={closeThen(onLogout)} />
              </VStack>

              {userName ? (
                <>
                  <Separator my={6} />
                  <HStack px={3} py={2} justify="space-between">
                    <Text>Eingeloggt als</Text>
                    <Text>{userName}</Text>
                  </HStack>
                </>
              ) : null}
            </Box>

            {/* Bottom CTA */}
            <Box pt={4}>
              <Button
                variant="subtle"
                colorPalette="purple"
                width="full"
                justifyContent="flex-start"
                // First need do rework onboardin for mobile
                // onClick={closeThen(onRestartOnboarding)}
              >
                <LuShieldQuestion />
                Einführung erneut starten
              </Button>
            </Box>
          </Flex>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
